import { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, Popover, Divider, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth0 } from '@auth0/auth0-react';
import { colors, layout } from '../theme/tokens';
import AiDashLogo from './AiDashLogo';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  borderBottom: `1px solid ${colors.border.header}`,
  zIndex: theme.zIndex.drawer + 1,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: layout.headerHeight,
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
}));

const UserPill = styled('button')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: colors.background.surface,
  borderRadius: 20,
  padding: '5px 10px 5px 5px',
  gap: 4,
  border: 'none',
  outline: 'none',
});

const AvatarCircle = styled('span')({
  width: 30,
  height: 30,
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const LogoutButton = styled(Button)({
  borderColor: colors.primary.main,
  color: colors.primary.dark,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  borderRadius: 4,
  padding: '8px 16px',
  '&:hover': {
    borderColor: colors.primary.dark,
    backgroundColor: 'rgba(0, 117, 149, 0.04)',
  },
});

const Header = () => {
  const { user, logout } = useAuth0();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };
  const displayName = user?.given_name || user?.name || user?.nickname || '';
  const displayEmail = user?.email || '';

  return (
    <StyledAppBar position="fixed" elevation={0}>
      <StyledToolbar>
        <AiDashLogo height={24} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.875rem', color: colors.text.primary, fontWeight: 500 }}>
            {displayName}
          </Typography>
          <UserPill onClick={handleMenuOpen}>
            <AvatarCircle>
              <PersonOutlineIcon sx={{ color: colors.text.primary, fontSize: 18 }} />
            </AvatarCircle>
            <ExpandMoreIcon
              sx={{
                color: colors.text.primary,
                fontSize: 16,
                transition: 'transform 0.2s ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </UserPill>
        </Box>

        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: '4px',
                minWidth: 380,
                boxShadow: '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
              },
            },
          }}
        >
          <Box sx={{ px: 3.5, py: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'rgba(0,0,0,0.87)' }}>
              {displayName}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: colors.text.secondary, mt: 0.5 }}>
              {displayEmail}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: colors.border.footer }} />
          <Box sx={{ px: 3, py: 2 }}>
            <LogoutButton
              variant="outlined"
              fullWidth
              startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
              onClick={handleLogout}
            >
              Log Out
            </LogoutButton>
          </Box>
        </Popover>
      </StyledToolbar>
    </StyledAppBar>
  );
};

export default Header;
